// Dip : cart code guard (loaded on EVERY page via theme.liquid).
//
// 2026-09-28 (Charlie, docs/dip-offer-quantity-cap-options-2026-09-28.md): the guard also holds the
// first-order offer's QUANTITY CAP. Before anything else on each cart change it trims any eligible
// subscription variant above the cap (theme setting dip_offer_qty_cap, island #dip-offer-cap in
// layout/theme.liquid), so one order cannot stock up at the offer price through the site. The basket
// steppers stop at the same number (snippets/cart-products.liquid) and the line says why.
//
// Keeps OUR discount codes in lockstep with cart state, using the cart update API's `discount`
// field (the same supported mechanism Horizon's cart-discount.js uses, NOT the legacy /discount/
// cookie, which persists after the cart stops qualifying and once let a gift-only cart check out
// at £0 after the qualifying items were removed).
//
// Offer shape (published to localStorage `dip:offers` by the buy surfaces, which are the only
// places that know the product model): { code, trigger: [variantIds], reward: [variantIds],
// triggerSub?: bool (default true), rewardSub?: bool (default true), blockedBy?: [variantIds],
// priority?: number (default 0) }. Rule per offer: the code belongs on the cart iff BOTH a
// trigger variant and a reward variant are present (as subscription lines unless the flags say
// otherwise). Shopify honours ONE product-class code per cart, so qualifying offers are scored
// and only the highest priority goes on.
//
// 2026-09-04 (offer decision ruling 1, build phase 2): the welcome kit left order 1, so the
// FREE-MSC-LD* / FREE-LDY-MSC1 gift codes and the DUO5 sweetener are no longer published.
// 2026-09-05 (ruling-11 fix b): the multi-surface hardware became zero-priced INCLUDED listings,
// so MSC-KIT-1 is no longer published either and NO code is guard-managed today. The machinery
// stays: any code the buy surfaces publish through assets/dip-hardware.js is handled here.
// The offer store is VERSIONED (`dip:offers:v`): a returning visitor whose browser still holds
// the retired gift entries has them wiped before anything is applied, and the pre-2026-07 single
// key `dip:msc-code` is dropped unread. Other discount codes on the cart are always preserved;
// we only add/remove OUR codes.
//
// 2026-09-04 (build phase 3 review): the guard also keeps the INCLUDED-HARDWARE quantities honest.
// Every multi-surface pack brings bottle + cloth in per-size quantities (1 + 1 on a 1-pack, 2 + 2
// on bigger packs) as zero-priced included lines, and the count must equal what the packs in the
// basket need (a pack removed must take its hardware with it). The buy surfaces publish the model
// (`dip:hardware`: the two hardware variants + every multi-surface variant's quantities, via
// assets/dip-hardware.js) and tag their hardware lines with the hidden property `_dip_hw`; after
// every cart change the guard sets those lines to the LARGEST need among the multi-surface packs
// present (0 when none remain), so removing a pack in the drawer can never leave "free" hardware
// billing, and a paid extra set (no property) is never touched.
(() => {
  const OFFERS_KEY = 'dip:offers';
  const VERSION_KEY = 'dip:offers:v';
  const HW_KEY = 'dip:hardware';
  // Keep in step with OFFERS_VERSION in dip-hardware.js (the publisher). 3 = fix (b), 2026-09-05.
  const OFFERS_VERSION = '3';
  // Codes Dip once guard-managed and has RETIRED (the BXGY-era hardware code, the welcome-kit gift
  // codes, the DUO5 sweetener). A returning visitor's persistent cart can still carry one as "not
  // applicable" (seen 2026-09-05 on the preview after fix b); the guard strips them like a revoked
  // offer so checkout never shows a dead code. Upper-case; the retirement list only ever grows.
  // 2026-09-28: FREE-MSC-LD6 and MSC-KIT-2/3/6 added when all seven kit codes were deactivated.
  const RETIRED = ['MSC-KIT-1', 'FREE-MSC-LD1', 'FREE-MSC-LD2', 'FREE-MSC-LD3', 'FREE-LDY-MSC1', 'DUO5',
    'FREE-MSC-LD6', 'MSC-KIT-2', 'MSC-KIT-3', 'MSC-KIT-6'];
  let busy = false, queued = false, timer = null, pendingCart = null;

  // Wipe stale entries from an older offer set (the retired welcome-kit codes) exactly once.
  try {
    localStorage.removeItem('dip:msc-code');
    if (localStorage.getItem(VERSION_KEY) !== OFFERS_VERSION) {
      localStorage.removeItem(OFFERS_KEY);
      Object.keys(localStorage).filter((k) => k.startsWith('dip:offer-slot:')).forEach((k) => localStorage.removeItem(k));
      localStorage.setItem(VERSION_KEY, OFFERS_VERSION);
    }
  } catch { /* storage unavailable: nothing to wipe */ }

  const offers = () => {
    const out = new Map(); // upper-cased code -> offer
    try {
      const multi = JSON.parse(localStorage.getItem(OFFERS_KEY) || 'null');
      if (multi && typeof multi === 'object') {
        Object.values(multi).forEach((o) => {
          if (o && o.code && Array.isArray(o.trigger) && Array.isArray(o.reward) && o.reward.length) {
            out.set(o.code.toUpperCase(), o);
          }
        });
      }
    } catch { /* ignore bad JSON */ }
    return [...out.values()];
  };

  // First-order offer quantity cap (Charlie 2026-09-28, docs/dip-offer-quantity-cap-options-2026-09-28.md).
  // The island (layout/theme.liquid, #dip-offer-cap) says whether the offer renders for this shopper,
  // the cap per item, and the eligible sizes by product handle (theme setting dip_offer_sizes). A line
  // counts when it is a SUBSCRIPTION line on an eligible size that is not already zeroed, which is the
  // rule snippets/dip-cart-line-offer.liquid applies. Counted per variant across all its lines.
  const capModel = () => {
    try {
      const el = document.getElementById('dip-offer-cap');
      if (!el) return null;
      const m = JSON.parse(el.textContent || 'null');
      const cap = Math.floor(Number(m && m.cap) || 0);
      if (!m || m.on !== true || cap < 1 || !m.sizes || typeof m.sizes !== 'object') return null;
      return { cap, sizes: m.sizes };
    } catch { return null; }
  };

  // Size = the last option whose name is not a scent-style name, byte for byte as the Liquid rule.
  const lineSize = (it) => {
    let size = '';
    (it.options_with_values || []).forEach((o) => {
      if (!/scent|fragrance|type|flavour/.test(String(o.name || '').toLowerCase())) size = o.value;
    });
    return size;
  };
  const capCounts = (it, sizes) => {
    if (!(it.selling_plan_allocation && it.selling_plan_allocation.selling_plan)) return false;
    if (!(Number(it.final_line_price) > 0)) return false;
    const list = sizes[it.handle];
    return Array.isArray(list) && list.includes(lineSize(it));
  };

  // Trim any variant over the cap: earlier lines keep their units first, later lines give way
  // (change.js by line key, quantity 0 removes). Returns true when it changed the cart.
  const clampOffer = async (cart, sectionIds, announce) => {
    const model = capModel();
    if (!model) return false;
    const kept = new Map();
    const plan = [];
    (cart.items || []).forEach((it) => {
      if (!capCounts(it, model.sizes)) return;
      const vid = String(it.variant_id);
      const qty = Number(it.quantity) || 0;
      const already = kept.get(vid) || 0;
      const keep = Math.max(0, Math.min(qty, model.cap - already));
      kept.set(vid, already + keep);
      if (keep !== qty) plan.push({ id: it.key, quantity: keep });
    });
    if (!plan.length) return false;
    let resp = null;
    for (const step of plan) {
      const body = { ...step };
      if (sectionIds.length) body.sections = sectionIds.join(',');
      resp = await (await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })).json();
    }
    if (!resp || !Array.isArray(resp.items)) {
      resp = await (await fetch(`/cart.js${sectionIds.length ? `?sections=${sectionIds.join(',')}` : ''}`, { headers: { Accept: 'application/json' } })).json();
    }
    announce(resp);
    return true;
  };

  const hardwareModel = () => {
    try {
      const m = JSON.parse(localStorage.getItem(HW_KEY) || 'null');
      return m && typeof m === 'object' && m.byVariant ? m : null;
    } catch { return null; }
  };

  // Included-hardware reconcile: the largest need among the multi-surface packs in the cart, set
  // on the property-tagged hardware lines (change.js by line key; add.js when a needed line is
  // missing). Returns true when it changed the cart (the resulting cart:update re-runs sync,
  // which then finds nothing to do).
  const reconcileHardware = async (cart, sectionIds, announce) => {
    const model = hardwareModel();
    if (!model) return false;
    const prop = model.prop || '_dip_hw';
    const items = cart.items || [];
    let needBottle = 0, needCloth = 0;
    items.forEach((it) => {
      const q = model.byVariant[String(it.variant_id)];
      if (!q) return;
      needBottle = Math.max(needBottle, Number(q.bottle) || 0);
      needCloth = Math.max(needCloth, Number(q.cloth) || 0);
    });
    const ours = (variantId) => items.filter((it) => Number(it.variant_id) === Number(variantId) && it.properties && it.properties[prop]);
    const plan = [];
    [[model.bottle, needBottle], [model.cloth, needCloth]].forEach(([vid, need]) => {
      if (!vid) return;
      const lines = ours(vid);
      const held = lines.reduce((n, it) => n + (Number(it.quantity) || 0), 0);
      if (held === need) return;
      if (lines.length) plan.push({ change: { id: lines[0].key, quantity: need } });
      else if (need > 0) plan.push({ add: { id: Number(vid), quantity: need, properties: { [prop]: '1' } } });
      // extra tagged lines beyond the first (should not happen) are zeroed
      lines.slice(1).forEach((it) => plan.push({ change: { id: it.key, quantity: 0 } }));
    });
    if (!plan.length) return false;
    let resp = null;
    for (const step of plan) {
      const body = step.change ? { ...step.change } : { items: [step.add] };
      if (sectionIds.length) body.sections = sectionIds.join(',');
      resp = await (await fetch(step.change ? '/cart/change.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })).json();
    }
    if (resp) {
      // add.js returns the added line(s), not the cart: fetch the cart so the morph is whole.
      if (!('item_count' in resp)) {
        resp = await (await fetch(`/cart.js${sectionIds.length ? `?sections=${sectionIds.join(',')}` : ''}`, { headers: { Accept: 'application/json' } })).json();
      }
      announce(resp);
    }
    return true;
  };

  const sync = async () => {
    const list = offers();
    if (!list.length && !hardwareModel() && !capModel()) return;
    if (busy) { queued = true; return; }
    busy = true;
    // Horizon's cart:update events carry the updated cart JSON (detail.resource): use it when it
    // looks like a full cart to skip a round-trip, so the revoke lands sooner after a removal.
    const fromEvent = pendingCart; pendingCart = null;
    try {
      const cart = (fromEvent && Array.isArray(fromEvent.items) && 'discount_codes' in fromEvent)
        ? fromEvent
        : await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      const items = cart.items || [];
      const isSub = (it) => !!(it.selling_plan_allocation && it.selling_plan_allocation.selling_plan);
      const hasSubLine = (ids) => items.some((it) => ids.includes(it.variant_id) && isSub(it));
      const hasLine = (ids) => items.some((it) => ids.includes(it.variant_id));

      const sectionIdsEarly = [...document.querySelectorAll('cart-items-component')]
        .map((el) => el.dataset.sectionId).filter(Boolean);
      const announceEarly = (resp) => {
        const ev = new Event('cart:update', { bubbles: true });
        ev.detail = {
          resource: resp,
          sourceId: 'dip-msc-guard',
          data: { source: 'dip-msc-guard', itemCount: resp.item_count, sections: resp.sections },
        };
        document.dispatchEvent(ev);
      };
      // The offer cap first: it can remove multi-surface packs, which the hardware step then follows.
      // Our own announcement does not re-trigger the listener, so queue a fresh pass on the new cart.
      if (await clampOffer(cart, sectionIdsEarly, announceEarly)) {
        queued = true;
        return;
      }
      // Hardware quantities next: the code decisions below depend on the reward lines existing.
      if (await reconcileHardware(cart, sectionIdsEarly, announceEarly)) {
        // The cart changed; the announced cart:update schedules a fresh sync on the new state.
        return;
      }
      const codes = (cart.discount_codes || []).map((d) => d.code);
      let next = [...codes];
      let changed = false;

      // Retired codes lingering on the cart (and in the legacy cookie) go first, unconditionally.
      RETIRED.forEach((code) => {
        const idx = next.findIndex((x) => x.toUpperCase() === code);
        if (idx !== -1) { next.splice(idx, 1); changed = true; }
        const raw = document.cookie.split('; ').find((r) => r.startsWith('discount_code='));
        const val = raw ? decodeURIComponent(raw.slice('discount_code='.length)) : '';
        if (val.toUpperCase() === code) document.cookie = 'discount_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
      });
      if (!list.length && !changed) return;

      const qualifiesFor = (o) => {
        if (Array.isArray(o.blockedBy) && o.blockedBy.length && hasSubLine(o.blockedBy)) return false;
        const triggerOk = o.triggerSub === false ? hasLine(o.trigger) : hasSubLine(o.trigger);
        const rewardOk = o.rewardSub === false ? hasLine(o.reward) : hasSubLine(o.reward);
        return triggerOk && rewardOk;
      };
      // One product-class code per cart: the highest-priority qualifying offer wins.
      const best = list
        .filter(qualifiesFor)
        .sort((a, b) => (Number(b.priority) || 0) - (Number(a.priority) || 0))[0];

      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((el) => el.dataset.sectionId).filter(Boolean);
      const announce = (resp) => {
        const ev = new Event('cart:update', { bubbles: true });
        ev.detail = {
          resource: resp,
          sourceId: 'dip-msc-guard',
          data: { source: 'dip-msc-guard', itemCount: resp.item_count, sections: resp.sections },
        };
        document.dispatchEvent(ev);
      };

      for (const o of list) {
        const qualifies = !!best && o.code.toUpperCase() === best.code.toUpperCase();
        const idx = next.findIndex((x) => x.toUpperCase() === o.code.toUpperCase());

        // Checkout also honours the legacy `discount_code` COOKIE (set by /discount/<code> links)
        // independently of the cart's discount state. Expire it whenever the cart doesn't qualify,
        // but only when it holds OUR code (never a customer's own). Not HttpOnly (verified).
        if (!qualifies) {
          const raw = document.cookie.split('; ').find((r) => r.startsWith('discount_code='));
          const val = raw ? decodeURIComponent(raw.slice('discount_code='.length)) : '';
          if (val.toUpperCase() === o.code.toUpperCase()) {
            document.cookie = 'discount_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
          }
        }

        if (qualifies && idx === -1) { next.push(o.code); changed = true; }
        else if (!qualifies && idx !== -1) { next.splice(idx, 1); changed = true; }
      }

      if (changed) {
        // Ask the update to render the cart sections on this page so we can morph them; without
        // this the revoke works server-side but the drawer keeps showing the stale line until a
        // reload. The announcement matches Horizon's CartUpdateEvent (plain Event + assigned
        // detail) so cart-items morphs the returned HTML.
        const body = { discount: next.join(',') }; // '' clears our codes when none remain
        if (sectionIds.length) body.sections = sectionIds.join(',');
        const resp = await (await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })).json();
        announce(resp);
      }
    } catch (e) {
      // Never break the storefront over a code; worst case the code is stale until the next event.
      console.warn('[dip-msc-guard]', e);
    } finally {
      busy = false;
      if (queued) { queued = false; schedule(); }
    }
  };

  // Debounce: cart events can arrive in bursts (morph + section re-renders). Kept short: the sync
  // is one cheap request (often zero, when the event carried the cart) and latency is user-visible.
  const schedule = () => { clearTimeout(timer); timer = setTimeout(sync, 50); };

  // Horizon ThemeEvents.cartUpdate bubbles from all cart components. Ignore our own announcement
  // (dispatched after apply/revoke) so a sync doesn't schedule another redundant cycle. Stash the
  // event's cart JSON (latest wins) so sync can skip its own /cart.js fetch.
  document.addEventListener('cart:update', (e) => {
    if (e.detail?.sourceId === 'dip-msc-guard') return;
    pendingCart = e.detail?.resource || null;
    schedule();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();
})();
